import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { TenantContext } from "../../pages/tenants/tenant-detail";
import { arrayAny } from "../../utils/arrayUtils";
import _ from "lodash";
import NumberHelpers from './../../app/number-helpers';
import Comparer from "../../app/comparer";
import { useMsal, } from '@azure/msal-react';
import UserApi from '../../api/user//user-api-factory';
import GraphApi from './../../api/graph/graph-api';
import { DebitCreditModal } from "./debit-credit-modal";

export const PaymentsTab = () => {

    const numberHelpers = useMemo(() => new NumberHelpers(), []);
    const comparer = useMemo(() => new Comparer(), []);

    const { instance, inProgress, accounts } = useMsal();

    const graphApi = useMemo(() => new GraphApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
    const userApi = useMemo(() => new UserApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const { tenant, setTenant, originalTenant, bankData, bankDataError } = useContext(TenantContext);

    const findCurrentTenancy = useCallback(() => {
        let latestInPeriods;
        let latestTenancy;
        for (const tenancy of tenant.tenancies) {
            if (!latestInPeriods || latestInPeriods < _.maxBy(tenancy.rentalPeriods, rp => new Date(rp.start))) {
                latestInPeriods = _.maxBy(tenancy.rentalPeriods, rp => new Date(rp.start));
                latestTenancy = tenancy;
            }
        }

        const result = latestTenancy ?? _.last(tenant.tenancies);

        result.debitsAndCredits ??= [];
        return result;
    }, [tenant]);

    const [debitsAndCredits, setDebitsAndCredits] = useState(() => structuredClone(findCurrentTenancy().debitsAndCredits ?? []));
    const [paymentAllocations, setPaymentAllocations] = useState(() => structuredClone(findCurrentTenancy().paymentAllocations ?? []));
    const [baseDebitsAndCredits, setBaseDebitsAndCredits] = useState(() => structuredClone(findCurrentTenancy().debitsAndCredits ?? []));
    const [basePaymentAllocations, setBasePaymentAllocations] = useState(() => structuredClone(findCurrentTenancy().paymentAllocations ?? []));
    const [allocationInputs, setAllocationInputs] = useState({}); // per-month input text to allow in-progress decimals
    const [showDebitCreditModal, setShowDebitCreditModal] = useState(false);
    const [selectedDC, setSelectedDC] = useState(null);

    const [myProfile, setMyProfile] = useState(null);
    const [fetchingUserData, setFetchingUserData] = useState(false);
    const [fetchUserDataComplete, setFetchUserDataComplete] = useState(false);

    const isCleanState = useMemo(() => {
        if (!originalTenant || !tenant) return false;
        return comparer.equals(tenant, originalTenant);
    }, [tenant, originalTenant, comparer]);

    // When the tenant state is reset back to the original (e.g. via Cancel on the parent form),
    // capture the current debits/credits and payment allocations as the new baseline and
    // clear any local allocation input overrides so the UI reflects the clean state.
    useEffect(() => {
        if (isCleanState) {
            const tenancy = findCurrentTenancy();
            // Take deep snapshots here so later in-place edits to
            // debits/credits or payment allocations do not mutate the
            // baseline used for projected balance calculations.
            setBaseDebitsAndCredits(structuredClone(tenancy.debitsAndCredits ?? []));
            setBasePaymentAllocations(structuredClone(tenancy.paymentAllocations ?? []));
            setAllocationInputs({});
        }
    }, [isCleanState, findCurrentTenancy]);

    // Keep local debitsAndCredits and paymentAllocations in sync with the tenant context.
    // This ensures that when the parent Cancel button restores the original tenant,
    // the Payments tab views and summaries also revert.
    useEffect(() => {
        const tenancy = findCurrentTenancy();
        // Work with local deep copies so edits in this tab don't
        // accidentally mutate the objects held in the context until
        // we intentionally push changes back via setTenant.
        setDebitsAndCredits(structuredClone(tenancy.debitsAndCredits ?? []));
        setPaymentAllocations(structuredClone(tenancy.paymentAllocations ?? []));
    }, [tenant, findCurrentTenancy]);

    const projectedBalance = useMemo(() => {
        if (!tenant) return null;

        const backendBalance = typeof tenant.balance === 'number'
            ? tenant.balance
            : numberHelpers.safeParseFloat(tenant.balance, 2);

        if (!Number.isFinite(backendBalance)) {
            return null;
        }

        const sumDebitsAndCredits = list => (list ?? []).reduce((acc, dc) => {
            const val = numberHelpers.safeParseFloat(dc.value, 2);
            return acc + (Number.isFinite(val) ? val : 0);
        }, 0);

        const sumPaymentAllocations = list => (list ?? []).reduce((acc, pa) => {
            const val = numberHelpers.safeParseFloat(pa.value, 2);
            return acc + (Number.isFinite(val) ? val : 0);
        }, 0);

        const baseDcTotal = sumDebitsAndCredits(baseDebitsAndCredits);
        const currentDcTotal = sumDebitsAndCredits(debitsAndCredits);
        const basePaTotal = sumPaymentAllocations(basePaymentAllocations);
        const currentPaTotal = sumPaymentAllocations(paymentAllocations);

        const deltaDebitCredit = currentDcTotal - baseDcTotal;
        const deltaPaymentAllocation = currentPaTotal - basePaTotal;

        // Treat positive debits/credits (credits) and positive allocations
        // as reducing any outstanding amount, and negative debits/credits
        // (debits) as increasing it. We model this by adjusting the
        // backend balance directly by the net change in these values.
        const deltaBalance = deltaDebitCredit + deltaPaymentAllocation;

        return backendBalance + deltaBalance;
    }, [tenant, baseDebitsAndCredits, basePaymentAllocations, debitsAndCredits, paymentAllocations, numberHelpers]);

    useEffect(() => {
        // If the Graph or User APIs are not properly initialised (e.g. in tests
        // or in edge startup conditions), skip fetching user data rather than
        // throwing. The call is an enhancement, not critical for rendering.
        if (!graphApi || typeof graphApi.getMeDetails !== 'function' || !userApi || typeof userApi.user !== 'function') {
            return;
        }

        const fetchUserData = async () => {
            setFetchingUserData(true);

            try {
                const graphUser = await graphApi.getMeDetails();
                if (graphUser) {
                    const backendUserId = graphUser.objectId || graphUser.id;
                    if (backendUserId) {
                        const systemUsr = await userApi.user(backendUserId);
                        setMyProfile(systemUsr);
                    }
                }
            } finally {
                setFetchingUserData(false);
                setFetchUserDataComplete(true);
            }
        };

        if (!fetchingUserData && !fetchUserDataComplete) {
            fetchUserData();
        }
    }, [graphApi, userApi, accounts, fetchingUserData, fetchUserDataComplete]);


    const generateMonths = (startDate, endDate) => {
        const months = [];
        const start = new Date(startDate);
        const end = new Date(endDate);

        while (start <= end) {
            const month = `${start.getFullYear()}-${('0' + (start.getMonth() + 1)).slice(-2)}`;
            months.push(month);
            start.setMonth(start.getMonth() + 1);
        }

        return months;
    };


    const summarizeTransactions = (transactions, debitsAndCredits, paymentAllocations, startDate, endDate) => {

        const summary = {};

        // Initialize summary with 0 for all months in the range
        const months = generateMonths(startDate, endDate);
        months.forEach(month => {
            summary[month] = { totalReceived: 0, totalDue: 0, settled: false, debitCredit: 0, paymentAllocation: 0 };
        });

        transactions.forEach(transaction => {

            const date = new Date(transaction.transactionDate);
            const month = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}`;
            if (month in summary) {
                summary[month].totalReceived += transaction.grandTotal;
            }
        });

        debitsAndCredits.forEach(dc => {

            const date = new Date(dc.date);
            const month = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}`;
            if (month in summary) {
                const val = Number(dc.value);
                summary[month].debitCredit += Number.isFinite(val) ? val : 0;
            }
        });

        paymentAllocations.forEach(pa => {

            const date = new Date(pa.date);
            const month = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}`;
            if (month in summary) {
                const val = Number(pa.value);
                summary[month].paymentAllocation += Number.isFinite(val) ? val : 0;
            }
        });

        for (const schedule of _.uniq(tenant.tenancies.flatMap(t => t.rentSchedule))) {

            for (const breakdown of _.chain(schedule.breakdown)
                .groupBy(s => s.date)
                .mapValues(group => _.minBy(group, 'rentDue'))) {
                if (breakdown.date in summary) {
                    summary[breakdown.date].totalDue += breakdown.rentDue;
                    summary[breakdown.date].settled = summary[breakdown.date].totalDue + summary[breakdown.date].debitCredit <= summary[breakdown.date].paymentAllocation
                }
            }
        }

        const currentTenancy = findCurrentTenancy();
        const currentStart = _.max(currentTenancy.rentalPeriods.flatMap(rp => new Date(rp.period.start)));
        const earliestTrn = _.min(transactions.flatMap(t => new Date(t.transactionDate)));
        const earliestPA = _.min(paymentAllocations.flatMap(pa => new Date(pa.date)));
        const earliestDC = _.min(debitsAndCredits.flatMap(dc => new Date(dc.date)));

        const filterDate = _.min([earliestTrn, earliestPA, earliestDC, currentStart]);
        filterDate.setDate(1);
        filterDate.setHours(0, 0, 0, 0);

        const finalResult = [];
        for (const line of Object.entries(summary)) {
            if (new Date(`${line[0]}-01`) >= filterDate && line[1].totalDue > 0) {
                finalResult.push(line);
            }
        }

        return _.orderBy(finalResult, [], ['desc']);
    };

    const getDateRange = () => {

        // Get the current date
        const currentDate = new Date();

        // Create a new date objects to avoid mutating the original date
        const earliestStartDate = new Date(currentDate);
        const latestEndDate = new Date(currentDate);

        // Subtract/Add 6 months
        earliestStartDate.setMonth(earliestStartDate.getMonth() - 6);
        latestEndDate.setMonth(latestEndDate.getMonth() + 6);

        return { earliestStartDate, latestEndDate };
    };

    const shortFallOrOverage = monthLine => {
        return (monthLine[1].paymentAllocation - totalDue(monthLine)).toFixed(2);
    };

    const totalReceivedInMonth = monthLine => monthLine[1].totalReceived > 0 ? monthLine[1].totalReceived.toFixed(2) : monthLine[1].paymentAllocation.toFixed(2);

    const totalDue = monthLine => (monthLine[1].totalDue - monthLine[1].debitCredit).toFixed(2);

    const getMonthYearString = date => {
        const newDate = new Date(date);
        const month = `${newDate.getFullYear()}-${('0' + (newDate.getMonth() + 1)).slice(-2)}`;
        return month;
    };

    const handlePaymentAllocation = (value, date) => {

        const newPaymentAllocations = [...paymentAllocations];
        const index = newPaymentAllocations.findIndex(pa => getMonthYearString(pa.date) === date);
        if (index !== -1) {
            newPaymentAllocations[index].value = numberHelpers.safeParseFloat(value, 2);
            newPaymentAllocations[index].createdBy ??= myProfile;
            newPaymentAllocations[index].created ??= new Date();
            newPaymentAllocations[index].modifiedBy = myProfile;
            newPaymentAllocations[index].modified = new Date();
        }
        else {
            newPaymentAllocations.push(
                {
                    value: numberHelpers.safeParseFloat(value, 2),
                    date: new Date(`${date}-01`),
                    createdBy: myProfile,
                    created: new Date(),
                    modifiedBy: myProfile,
                    modified: new Date()
                });
        }
        setPaymentAllocations(newPaymentAllocations);

        const tenancy = findCurrentTenancy();
        setTenant(current => {
            const tenancyIndex = current.tenancies.indexOf(tenancy);
            if (tenancyIndex > -1) {
                current.tenancies[tenancyIndex].paymentAllocations = newPaymentAllocations;
            }
            return structuredClone(current);
        });

    };

    const handleDeleteDC = dc => {
        const newDebitsAndCredits = debitsAndCredits.filter(d => d !== dc);
        setDebitsAndCredits(newDebitsAndCredits);

        const tenancy = findCurrentTenancy();
        setTenant(current => {
            const index = current.tenancies.indexOf(tenancy);
            if (index > -1) {
                current.tenancies[index].debitsAndCredits = newDebitsAndCredits;
            }
            return structuredClone(current);
        });
        setSelectedDC(null);
    };

    const handleDebitCreditChange = (dc, date) => {

        const newDebitsAndCredits = [...debitsAndCredits];
        const index = newDebitsAndCredits.findIndex(pa => pa.id === dc.id);
        if (index !== -1) {
            newDebitsAndCredits[index].type = dc.type;
            {
                const raw = numberHelpers.safeParseFloat(dc.value);
                const amount = Math.abs(typeof raw === 'number' ? raw : parseFloat(raw));
                newDebitsAndCredits[index].value = dc.type === 'debit' ? -1 * amount : amount;
            }
            newDebitsAndCredits[index].reason = dc.reason;
            newDebitsAndCredits[index].date = dc.date;
            newDebitsAndCredits[index].createdBy ??= myProfile;
            newDebitsAndCredits[index].created ??= new Date();
            newDebitsAndCredits[index].modifiedBy = myProfile;
            newDebitsAndCredits[index].modified = new Date();
        }
        else {
            newDebitsAndCredits.push(
                {
                    value: (() => {
                        const raw = numberHelpers.safeParseFloat(dc.value);
                        const amount = Math.abs(typeof raw === 'number' ? raw : parseFloat(raw));
                        return dc.type === 'debit' ? -1 * amount : amount;
                    })(),
                    reason: dc.reason,
                    type: dc.type,
                    date: dc.date,
                    createdBy: myProfile,
                    created: new Date(),
                    modifiedBy: myProfile,
                    modified: new Date()
                });
        }
        setDebitsAndCredits(newDebitsAndCredits);

        const tenancy = findCurrentTenancy();
        setTenant(current => {
            const index = current.tenancies.indexOf(tenancy);
            if (index > -1) {
                current.tenancies[index].debitsAndCredits = newDebitsAndCredits;
            }
            return structuredClone(current);
        });
        setShowDebitCreditModal(false);
        setSelectedDC(null);
    };


    return (
        <div className="tab-content panel p-3 rounded">
            <div className="tab-pane fade active show" id="default-tab-4">
                <h3>Payment History | {tenant.displayName}
                    {(() => {
                        const bal = projectedBalance;
                        return bal && Math.abs(bal) > 0.01
                            ? <> | Balance: <strong>{bal.toFixed(2)}</strong></>
                            : null;
                    })()}
                </h3>
                <p>
                    If you do not see payments listed, please check the tenant is linked to a Xero contact.
                </p>
                <div className="row gy-3 gx-2 gx-xl-5">
                    <div className="col-12 col-xl-4 table-responsive">
                        <h4>Recent and Upcoming Payments</h4>
                        <div>Calculated on the fly using forwards projection of rent and details of bank transactions fetched from Xero.</div>
                        <table className="mt-10px table table-bordered align-middle table-hover table-dark">
                            <thead className="thead-dark">
                                <tr>
                                    <th className="align-middle text-center d-none d-sm-table-cell" nowrap="true">#</th>
                                    <th className="align-middle text-center">Month</th>
                                    <th className="align-middle text-center d-none d-sm-table-cell">Rent Due</th>
                                    <th className="align-middle text-center d-none d-md-table-cell">Debits/ Credits</th>
                                    <th className="align-middle text-center">Total Due</th>
                                    <th className="align-middle text-center">Allocation</th>
                                    <th className="align-middle text-center">Total Received</th>
                                    <th className="align-middle text-center">Variance</th>
                                    <th className="align-middle text-center d-none d-sm-table-cell">Settled</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(summarizeTransactions(bankData ?? [], debitsAndCredits ?? [], paymentAllocations ?? [], getDateRange().earliestStartDate, getDateRange().latestEndDate).map((monthLine, i) =>
                                    <tr key={`${tenant.id}-${i}`} className={shortFallOrOverage(monthLine) >= 0 ? "table-success" : "table-warning"} >
                                        <td className="align-middle text-center d-none d-sm-table-cell" nowrap="true">{i + 1}</td>
                                        <td className="align-middle text-center" nowrap="true">{monthLine[0]}</td>
                                        <td className="align-middle text-center d-none d-sm-table-cell" nowrap="true">{monthLine[1].totalDue.toFixed(2)}</td>
                                        <td className="align-middle text-center d-none d-md-table-cell" nowrap="true">{monthLine[1].debitCredit.toFixed(2)}</td>
                                        <td className="align-middle text-center" nowrap="true">{totalDue(monthLine)}</td>
                                        <td className="align-middle text-center p-3px" nowrap="true">
                                            {(() => {
                                                const monthKey = monthLine[0];
                                                const baseValue = monthLine[1].paymentAllocation ? numberHelpers.safeParseFloat(monthLine[1].paymentAllocation, 2) : "";
                                                const inputValue = allocationInputs[monthKey];
                                                const displayValue = isCleanState ? baseValue : (inputValue ?? baseValue);
                                                return (
                                                    <input
                                                        className="align-middle text-center form-control ms-2px me-0"
                                                        style={{ maxWidth: '65px', width: '100%' }}
                                                        type="text"
                                                        inputMode="decimal"
                                                        placeholder="0"
                                                        value={displayValue}
                                                        onChange={e => {
                                                            e.preventDefault();
                                                            const val = e.target.value;
                                                            // Allow optional leading minus, digits, and a single optional decimal point (supports in-progress decimals like "50.")
                                                            if (/^-?\d*\.?\d*$/.test(val)) {
                                                                setAllocationInputs(current => ({ ...current, [monthKey]: val }));
                                                                handlePaymentAllocation(val, monthKey);
                                                            }
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </td>
                                        <td className="align-middle text-center" nowrap="true">{totalReceivedInMonth(monthLine)}</td>
                                        <td className="align-middle text-center" nowrap="true">{shortFallOrOverage(monthLine)}</td>
                                        <td className="align-middle text-center d-none d-sm-table-cell" nowrap="true">
                                            <div className={`badge bg-${shortFallOrOverage(monthLine) >= 0 ? 'success' : 'danger'}`}>
                                                <i className={`fas fa-lg fa-fw fa-${shortFallOrOverage(monthLine) >= 0 ? 'check' : 'x'}`} />
                                            </div>
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {!!!bankDataError &&
                        <div className="col-12 col-xl-4 table-responsive">
                            <h4>Bank Transactions</h4>
                            <div>These records have been retrieved from Xero and cached in the system for posterity. This shows money flowing to and from the Tenant.</div>
                            <table className="mt-10px table table-bordered align-middle table-hover table-dark">
                                <thead>
                                    <tr>
                                        <th className="align-middle text-center" nowrap="true">#</th>
                                        <th className="align-middle text-center" nowrap="true">Date</th>
                                        <th className="align-middle text-center" nowrap="true">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(bankData && arrayAny(bankData) && _.orderBy(bankData, bt => bt.transactionDate, ["desc"]).map((transaction, i) =>
                                        <tr key={`${tenant.id}-${i}`} className="table-light" >
                                            <td title={transaction.bankTransactionId} className="align-middle text-center" nowrap="true">{i + 1}</td>
                                            <td className="align-middle text-center" nowrap="true">{new Date(transaction.transactionDate).toLocaleDateString()}</td>
                                            <td className="align-middle text-center" nowrap="true">{transaction.grandTotal.toFixed(2) > 0 ? `+${transaction.grandTotal.toFixed(2)}` : `(${transaction.grandTotal.toFixed(2)})`}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    }
                    <div className={!!!bankDataError ? "col-12 col-xl-4 table-responsive" : "col-12 col-xl-6 table-responsive"}>
                        <h4>Debits and Credits</h4>
                        <div>Use Debits for things like chargeable items such as lockouts, new keys, energy bill contributions. Use Credits for refunds or initial rent incentives.</div>

                        <DebitCreditModal
                            handleCancel={() => setShowDebitCreditModal(false)}
                            handleSave={handleDebitCreditChange}
                            show={showDebitCreditModal}
                            selectedDC={selectedDC}
                        />

                        <button type="button" className="btn btn-primary mt-2" data-bs-toggle="dropdown">
                            Action&nbsp;&nbsp;<span className="caret"></span>
                        </button>
                        <div className="dropdown-menu">
                            <button type="button" onClick={() => setShowDebitCreditModal(true)} className="dropdown-item"><i className="fa fa-plus"></i>&nbsp;&nbsp;Add Debit/Credit</button>
                        </div>


                        <table className="mt-10px table table-bordered align-middle table-hover table-dark">
                            <thead>
                                <tr>
                                    <th className="align-middle text-center" nowrap="true">#</th>
                                    <th className="align-middle text-center" nowrap="true">Date</th>
                                    <th className="align-middle text-center" nowrap="true">Amount</th>
                                    <th className="align-middle text-center" nowrap="true">Reason</th>
                                    <th className="align-middle text-center" nowrap="true"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(debitsAndCredits && arrayAny(debitsAndCredits) && _.orderBy(debitsAndCredits, dc => dc.date, ["desc"]).map((dc, i) =>
                                    <tr key={`${tenant.id}-${i}`} className="table-light" >
                                        <td className="align-middle text-center" nowrap="true">{i + 1}</td>
                                        <td className="align-middle text-center" nowrap="true">{new Date(dc.date).toLocaleDateString()}</td>
                                        <td className="align-middle text-center" nowrap="true">{dc.value.toFixed(2) > 0 ? `+${dc.value.toFixed(2)}` : `(${dc.value.toFixed(2)})`}</td>
                                        <td className="align-middle text-center" nowrap="true">{dc.reason}</td>
                                        <td className="align-middle" nowrap="true">
                                            <button type="button" className="btn btn-primary mt-2 text-ellipsis" data-bs-toggle="dropdown">
                                                Action&nbsp;&nbsp;<span className="caret"></span>
                                            </button>
                                            <div className="dropdown-menu">
                                                <button type="button" onClick={() => { setShowDebitCreditModal(true); setSelectedDC(dc) }} className="dropdown-item"><i className="fa fa-pencil" ></i>&nbsp;&nbsp;Edit Debit/Credit</button>
                                                <button type="button" onClick={() => handleDeleteDC(dc)} className="dropdown-item"><i className="fa fa-trash" ></i>&nbsp;&nbsp;Delete Debit/Credit</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};